import { ImportError } from '@utils/errors';
import type { ErrorEvent, SeverityLevel } from '@sentry/types';

export interface ExceptionLike {
    type?: string;
    value?: string;
}
export interface ErrorEventLike {
    type: undefined;
    level?: SeverityLevel;
    exception?: {
        values?: ExceptionLike[];
    };
}
const dynamicallyImportedModuleErrorMessages = [
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
];

export const isDynamicallyImportedModuleError = (
    errorMessage: string,
    castToWarnErrorMessagesArray = dynamicallyImportedModuleErrorMessages
) => {
    if (!errorMessage || !castToWarnErrorMessagesArray.length) return false;
    return castToWarnErrorMessagesArray
        .filter((message) => !!message)
        .some((message) => new RegExp(message).test(errorMessage));
};

export const castExceptionLevel = <T extends ErrorEventLike>(
    event: T,
    level?: ErrorEventLike['level']
) => {
    if (!event.level || !level) return event;
    const eventCopy = {
        ...event,
        level,
    };
    return eventCopy;
};

export const castExceptionType = <T extends ErrorEventLike>(
    event: T,
    type?: string
): ErrorEventLike => {
    if (!type || !event.exception?.values?.[0]?.type) return event;
    const eventCopy = {
        ...event,
    };
    if (eventCopy.exception?.values?.[0]?.type) {
        eventCopy.exception.values[0].type = type;
    }
    return eventCopy;
};

export const extractExceptionData =
    (property: keyof ExceptionLike) =>
    (event: ErrorEventLike, index = 0) =>
        event.exception?.values?.[index][property];

export const extractExceptionMessage = extractExceptionData('value');
export const extractExceptionType = extractExceptionData('type');

export const castDynamicallyImportedModuleError = (event: ErrorEventLike) => {
    const castedLevel = castExceptionLevel(event, 'warning');
    const castedType = castExceptionType(castedLevel, ImportError.name);
    return castedType;
};

export const handleCasting = (errorMessage: string, event: ErrorEventLike) => {
    if (isDynamicallyImportedModuleError(errorMessage)) {
        return castDynamicallyImportedModuleError(event);
    }
    return event;
};

export const handleBeforeSend = (event: ErrorEventLike) => {
    const errorMessage = extractExceptionMessage(event);
    if (!errorMessage) return event;

    return handleCasting(errorMessage, event);
};

export const onBeforeSend = (event: ErrorEvent) => {
    try {
        return handleBeforeSend(event);
    } catch (error: unknown) {
        return event;
    }
};
